/**
 * The one HTTP entry point for the whole app. Orval's generated client calls
 * `customFetch(url, options)` for every request; this is the only place base
 * URL, auth and error handling live. No hand-written fetch anywhere else.
 */

let baseUrl = "";
let authToken: string | null = null;

/** Point the client at a backend. Empty (default) keeps `/v1/...` same-origin,
 *  which is what MSW mocks intercept during development. Set to the live base
 *  (e.g. `https://api.solar-key.com`) to flip a screen from mock to live. */
export function setApiBaseUrl(url: string): void {
  baseUrl = url.replace(/\/$/, "");
}

/** Set (or clear with `null`) the bearer token sent on every request. */
export function setAuthToken(token: string | null): void {
  authToken = token;
}

let onUnauthorized: (() => void) | null = null;

/** Register a handler fired when an authed request returns 401 (e.g. the token
 *  expired). The app uses it to clear the session and return to the login screen,
 *  so an expired token never strands the user on a dead "API error 401". */
export function setUnauthorizedHandler(fn: (() => void) | null): void {
  onUnauthorized = fn;
}

/** Thrown for any non-2xx response, carrying the status and the parsed body so
 *  callers (and TanStack Query) can branch on it. */
export class ApiError extends Error {
  readonly status: number;
  readonly body: unknown;
  constructor(status: number, body: unknown) {
    super(`API error ${status}`);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
  }
}

export const customFetch = async <T>(
  url: string,
  options?: RequestInit,
): Promise<T> => {
  const headers = new Headers(options?.headers);
  if (!headers.has("Content-Type") && options?.body) {
    headers.set("Content-Type", "application/json");
  }
  if (authToken) headers.set("Authorization", `Bearer ${authToken}`);

  const response = await fetch(`${baseUrl}${url}`, { ...options, headers });

  const text = await response.text();
  const body: unknown = text ? safeJson(text) : undefined;

  if (response.status === 401 && authToken) {
    // An authed request was rejected — the token is expired/invalid. Clear the
    // session so the shell returns to login instead of stranding on an error.
    onUnauthorized?.();
  }
  if (!response.ok) throw new ApiError(response.status, body);

  // Orval's fetch client types every response as `{ data, status, headers }`;
  // the generated hooks read `.data` off this wrapper.
  return { data: body, status: response.status, headers: response.headers } as T;
};

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}
